import {
  ApolloError,
  MutationFunctionOptions,
  MutationHookOptions,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {getOperationName} from './AST';

/**
 * The stable, non-`data` fields returned alongside the mutate function, mirroring Apollo's
 * `useMutation` result.
 */
export interface BenchlingMutationResult {
  loading: boolean;
  called: boolean;
  error: ApolloError | undefined;
  reset: () => void;
}

/**
 * The normalized mutate function. Resolves to the mutation's typed `data` on success and throws (like our
 * REST DAOs did) if the operation hard-errors — a network error, a thrown resolver, or a response missing
 * `data`.
 *
 * Expected / validation failures should be returned in-band as part of `TData` via a union arm (e.g.
 * `Foo | ErrorNode`); callers discriminate those with a type guard such as `isError`. Hard errors are also
 * surfaced to the user automatically by `createErrorLink`, so callers that rethrow should NOT add their own
 * generic toast for hard errors (though they may still toast for an in-band error arm).
 *
 * The gql document is fixed by the hook, so `mutation` is omitted from the per-call options a caller may
 * pass (callers set `variables`, `context`, etc.).
 */
export type BenchlingMutateFn<TData, TVariables extends OperationVariables> = (
  options?: Omit<MutationFunctionOptions<TData, TVariables>, 'mutation'>
) => Promise<TData>;

export type BenchlingMutationTuple<TData, TVariables extends OperationVariables> = [
  BenchlingMutateFn<TData, TVariables>,
  BenchlingMutationResult,
];

/**
 * Hook that wraps Apollo's `useMutation` with a more opinionated interface, paralleling
 * {@link useBenchlingQuery}. Whereas queries are render-time (and so use the `QueryResultType`
 * discriminated result), mutations are imperative: the caller awaits the returned `mutate` function and
 * gets back the typed `data`, or a thrown error for hard failures.
 *
 * Union-arm discrimination (e.g. `AntibodyFormatsNode | ErrorNode`) is intentionally left to the caller,
 * since this hook cannot know each mutation's union shape. Feature hooks should own the gql document and
 * the `isError` (or similar) handling.
 *
 * Usage:
 * @example
 *    const [mutate] = useBenchlingMutation<UpdateFoo, UpdateFooVariables>(UPDATE_FOO);
 *    const data = await mutate({variables: {input}});
 *    if (isError(data.updateFoo)) {
 *      notifications.error(data.updateFoo.message);
 *    } else {
 *      // success
 *    }
 */
export function useBenchlingMutation<TData, TVariables extends OperationVariables = OperationVariables>(
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
): BenchlingMutationTuple<TData, TVariables> {
  // Pin `errorPolicy: 'none'` (Apollo's default, but we don't want to rely on that staying the default or on
  // callers not flipping it) so the throw-on-hard-failure contract below holds regardless of Apollo config.
  // Under 'none', Apollo rejects the promise on GraphQL errors, so `apolloMutate` throws before we inspect
  // the result. Callers can still override per-hook if they explicitly want partial results.
  const [apolloMutate, {loading, called, error, reset}] = useMutation<TData, TVariables>(mutation, {
    errorPolicy: 'none',
    ...options,
  });

  const mutate: BenchlingMutateFn<TData, TVariables> = async (mutateOptions) => {
    const operationName = getOperationName(mutation);
    // Apollo rejects the promise on network / graphQLErrors under 'none', so we usually won't reach the
    // checks below; `createErrorLink` has already toasted for those. We rethrow so imperative callers
    // can run their own catch/finally, matching the throw-on-failure contract of the REST code we replace.
    const result = await apolloMutate(mutateOptions);
    // Defense in depth: a caller that overrides to `errorPolicy: 'all'`/'ignore' makes Apollo resolve
    // (rather than reject) even when the response carries GraphQL errors, with `data` possibly partial.
    // Treat any surfaced errors as a hard failure so callers don't silently operate on a partial result.
    if (result.errors != null && result.errors.length > 0) {
      throw new ApolloError({graphQLErrors: result.errors});
    }
    if (result.data == null) {
      throw new Error(`Mutation ${operationName} returned no data`);
    }
    return result.data;
  };

  return [mutate, {loading, called, error, reset}];
}
